<div class="space-y-6">
  <app-page-header
    title="Pièces KYC"
    subtitle="Le parc documentaire, tous acteurs confondus — trié par échéance, les expirations en tête. Valider une pièce arme son suivi d'expiration (rappels J-30 / J-15 / J-7) ; ça ne valide pas le dossier d'un opérateur, qui se traite depuis sa fiche."
  />

  <section class="rounded-2xl border border-gray-200 bg-white shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
    <div class="flex flex-col gap-3 border-b border-gray-200 px-5 py-4 dark:border-gray-800 lg:flex-row lg:items-center lg:justify-between">
      <p class="text-sm text-gray-700 dark:text-gray-300">
        @if (totalItems() === 0) {
          Aucune pièce.
        } @else {
          {{ totalItems() }} pièce(s)
        }
      </p>
      <div class="flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
        <div class="w-44">
          <app-pretty-select
            [options]="statusOptions"
            [ngModel]="statusFilter()"
            (ngModelChange)="changeStatus($event ?? null)"
            placeholder="Tous statuts"
            clearable
          />
        </div>
        <div class="w-40">
          <app-pretty-select
            [options]="subjectOptions"
            [ngModel]="subjectFilter()"
            (ngModelChange)="changeSubject($event ?? null)"
            placeholder="Tous acteurs"
            clearable
          />
        </div>
        <span>Par page</span>
        <div class="w-20">
          <app-pretty-select
            [options]="pageSizeOptions"
            [ngModel]="size()"
            (ngModelChange)="changeSize($event ?? 20)"
            placeholder="20"
          />
        </div>
      </div>
    </div>

    @if (error() || (loading() && items().length === 0) || items().length === 0) {
      <app-data-state
        [loading]="loading()"
        [error]="error()"
        [empty]="!loading() && !error() && items().length === 0"
        loadingMessage="Chargement des pièces…"
        emptyMessage="Aucune pièce ne correspond à ces filtres."
        (retry)="load()"
      />
    } @else {
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
          <thead class="bg-gray-50 dark:bg-white/[0.02]">
            <tr>
              <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Sujet</th>
              <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Gérant</th>
              <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Document</th>
              <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Statut</th>
              <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Émission</th>
              <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Expiration</th>
              <th class="px-5 py-3 text-right text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white dark:divide-gray-800 dark:bg-transparent">
            @for (doc of items(); track doc.id) {
              <tr class="transition hover:bg-gray-50 dark:hover:bg-white/[0.02]">
                <td class="whitespace-nowrap px-5 py-4">
                  <p class="text-sm font-medium text-gray-800 dark:text-white/90">{{ subjectDisplay(doc) }}</p>
                  @if (doc.subjectType === 'OPERATOR') {
                    <a [routerLink]="['/operators', doc.subjectRef]" fragment="kyc" class="text-xs text-brand-600 hover:text-brand-700 dark:text-brand-400">
                      Voir le dossier complet →
                    </a>
                  } @else if (doc.subjectType === 'OWNER') {
                    <a [routerLink]="['/owners', doc.subjectRef]" class="text-xs text-brand-600 hover:text-brand-700 dark:text-brand-400">
                      Voir le bailleur →
                    </a>
                  }
                </td>
                <!-- `user` n'est résolu que pour les sujets OPERATOR (cf. KycDocumentView). -->
                <td class="whitespace-nowrap px-5 py-4">
                  @if (doc.user) {
                    <app-user-cell [user]="doc.user" size="xs" />
                  } @else {
                    <span class="text-sm text-gray-400 dark:text-gray-500">—</span>
                  }
                </td>
                <td class="whitespace-nowrap px-5 py-4">
                  <p class="text-sm text-gray-700 dark:text-gray-300">{{ docTypeLabel[doc.documentType] }}</p>
                  <a [href]="doc.documentUrl" target="_blank" rel="noopener" class="text-xs text-brand-600 hover:text-brand-700 dark:text-brand-400">
                    Ouvrir le fichier →
                  </a>
                </td>
                <td class="whitespace-nowrap px-5 py-4">
                  <span
                    class="inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium"
                    [class.bg-brand-50]="statusMeta[doc.kycStatus].palette === 'brand'"
                    [class.text-brand-700]="statusMeta[doc.kycStatus].palette === 'brand'"
                    [class.dark:bg-brand-500/15]="statusMeta[doc.kycStatus].palette === 'brand'"
                    [class.dark:text-brand-400]="statusMeta[doc.kycStatus].palette === 'brand'"
                    [class.bg-warning-50]="statusMeta[doc.kycStatus].palette === 'warning'"
                    [class.text-warning-700]="statusMeta[doc.kycStatus].palette === 'warning'"
                    [class.dark:bg-warning-500/15]="statusMeta[doc.kycStatus].palette === 'warning'"
                    [class.dark:text-orange-400]="statusMeta[doc.kycStatus].palette === 'warning'"
                    [class.bg-error-50]="statusMeta[doc.kycStatus].palette === 'error'"
                    [class.text-error-700]="statusMeta[doc.kycStatus].palette === 'error'"
                    [class.dark:bg-error-500/15]="statusMeta[doc.kycStatus].palette === 'error'"
                    [class.dark:text-error-400]="statusMeta[doc.kycStatus].palette === 'error'"
                    [class.bg-gray-100]="statusMeta[doc.kycStatus].palette === 'gray'"
                    [class.text-gray-700]="statusMeta[doc.kycStatus].palette === 'gray'"
                    [class.dark:bg-gray-800]="statusMeta[doc.kycStatus].palette === 'gray'"
                    [class.dark:text-gray-300]="statusMeta[doc.kycStatus].palette === 'gray'"
                  >{{ statusMeta[doc.kycStatus].label }}</span>
                  @if (doc.kycStatus === 'REJECTED' && doc.rejectionReason) {
                    <p class="mt-0.5 max-w-[220px] truncate text-xs text-error-600 dark:text-error-400" [title]="doc.rejectionReason">
                      Motif : {{ doc.rejectionReason }}
                    </p>
                  }
                </td>
                <td class="whitespace-nowrap px-5 py-4 text-sm text-gray-700 dark:text-gray-300">
                  {{ doc.issueDate ? (doc.issueDate | ldate:'dd/MM/yyyy') : '—' }}
                </td>
                <!-- Colonne clé de cet écran : l'échéance, teintée dès qu'elle approche. -->
                <td class="whitespace-nowrap px-5 py-4 text-sm">
                  @if (doc.expiryDate) {
                    <span
                      [class.text-error-600]="expiryState(doc) === 'expired'"
                      [class.dark:text-error-400]="expiryState(doc) === 'expired'"
                      [class.font-medium]="expiryState(doc) === 'expired' || expiryState(doc) === 'soon'"
                      [class.text-warning-700]="expiryState(doc) === 'soon'"
                      [class.dark:text-orange-400]="expiryState(doc) === 'soon'"
                      [class.text-gray-700]="expiryState(doc) === 'ok'"
                      [class.dark:text-gray-300]="expiryState(doc) === 'ok'"
                    >{{ doc.expiryDate | ldate:'dd/MM/yyyy' }}</span>
                    @if (expiryHint(doc); as hint) {
                      <p
                        class="text-xs"
                        [class.text-error-600]="expiryState(doc) === 'expired'"
                        [class.dark:text-error-400]="expiryState(doc) === 'expired'"
                        [class.text-warning-700]="expiryState(doc) === 'soon'"
                        [class.dark:text-orange-400]="expiryState(doc) === 'soon'"
                      >{{ hint }}</p>
                    }
                  } @else {
                    <span class="text-gray-400 dark:text-gray-500">Sans échéance</span>
                  }
                </td>
                <td class="whitespace-nowrap px-5 py-4 text-right">
                  <div class="inline-flex items-center gap-2">
                    @if (doc.kycStatus !== 'VALIDATED') {
                      <button
                        type="button"
                        (click)="validate(doc)"
                        [disabled]="validatingId() !== null"
                        class="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        @if (validatingId() === doc.id) { … } @else { Valider }
                      </button>
                    }
                    @if (doc.kycStatus !== 'REJECTED') {
                      <button
                        type="button"
                        (click)="openReject(doc)"
                        class="inline-flex items-center justify-center gap-1.5 rounded-lg border border-error-300 px-3 py-1.5 text-xs font-medium text-error-700 transition hover:bg-error-50 dark:border-error-800 dark:text-error-400 dark:hover:bg-error-500/10"
                      >
                        Rejeter
                      </button>
                    }
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      @if (totalPages() > 0) {
        <div class="flex flex-col gap-3 border-t border-gray-200 px-5 py-4 dark:border-gray-800 sm:flex-row sm:items-center sm:justify-between">
          <p class="text-xs text-gray-500 dark:text-gray-400">
            Page {{ page() }} sur {{ totalPages() }} — {{ totalItems() }} pièce(s)
          </p>
          <div class="flex flex-wrap items-center gap-1">
            <button type="button" (click)="goToPage(page() - 1)" [disabled]="page() <= 1" class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-white/5">Précédent</button>
            @for (n of pageWindow(); track n) {
              <button
                type="button"
                (click)="goToPage(n)"
                class="min-w-[36px] rounded-lg border px-3 py-1.5 text-xs font-medium transition"
                [class.border-brand-500]="n === page()"
                [class.bg-brand-500]="n === page()"
                [class.text-white]="n === page()"
                [class.border-gray-300]="n !== page()"
                [class.text-gray-700]="n !== page()"
                [class.hover:bg-gray-50]="n !== page()"
                [class.dark:border-gray-700]="n !== page()"
                [class.dark:text-gray-400]="n !== page()"
                [class.dark:hover:bg-white/5]="n !== page()"
              >
                {{ n }}
              </button>
            }
            <button type="button" (click)="goToPage(page() + 1)" [disabled]="page() >= totalPages()" class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-white/5">Suivant</button>
          </div>
        </div>
      }
    }
  </section>
</div>

<!-- Reject modal -->
<app-modal [isOpen]="rejectOpen()" (close)="closeReject()" className="max-w-md p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">Rejeter la pièce</h3>
  @if (rejectTarget(); as t) {
    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
      Sujet : <strong>{{ subjectDisplay(t) }}</strong> — type : <strong>{{ docTypeLabel[t.documentType] }}</strong>
    </p>
  }

  @if (rejectError()) {
    <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ rejectError() }}
    </div>
  }

  <form [formGroup]="rejectForm" (ngSubmit)="submitReject()" class="mt-5 space-y-4">
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Raison du rejet *</label>
      <textarea
        rows="4"
        formControlName="reason"
        placeholder="Ex. : document illisible, type incorrect, expiration trop courte…"
        class="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
      ></textarea>
      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
        La raison s'affichera sur ce document dans l'espace KYC du titulaire (pas de notification envoyée).
      </p>
    </div>

    <div class="flex justify-end gap-2 pt-2">
      <button type="button" (click)="closeReject()" [disabled]="rejecting()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">Annuler</button>
      <button type="submit" [disabled]="rejecting() || rejectForm.invalid" class="rounded-lg bg-error-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-error-600 disabled:cursor-not-allowed disabled:opacity-50">
        @if (rejecting()) { Rejet… } @else { Confirmer le rejet }
      </button>
    </div>
  </form>
</app-modal>
